<script>
import Vue from 'vue';
import store from '@/store';
import i18n from '@/services/i18n';
import { loadScript } from '@/utils';

import nunito from '!!raw-loader!@/assets/fonts/nunito.css';
import staticFormCss from '!!raw-loader!sass-loader!./widget.scss';
import css from '!!raw-loader!sass-loader!@/assets/css/checkout/index.scss';
var MutationObserver = window.MutationObserver || window.WebKitMutationObserver;

// Todo make reusable component once we crack this
export default {
  data() {
    return {
      mutationObserver: null,
      resizeObserver: null,
      resizeTimeout: null,
      isResizing: false,
      iframeHeight: 362
    };
  },

  render(h) {
    return h('iframe', {
      on: { load: this.renderChildren },
      attrs: {
        src: 'about:blank',
        frameborder: 0,
        title: 'Donation Form',
        width: '100%',
        scrolling: 'no' // Prevent scrollbars
      },
      style: {
        width: '100%',
        maxWidth: '470px',
        height: this.iframeHeight + 'px',
        border: 'none',
        overflow: 'hidden'
      }
    });
  },

  watch: {
    css(newValue, oldValue) {
      if (!oldValue && newValue) {
        this.applyCss();
      }
    }
  },

  methods: {
    renderChildren() {
      const children = this.$slots.default;
      const body = this.$el.contentDocument.body;
      const div = document.createElement('DIV');

      // Ensure body has no margin/padding that could affect measurements
      body.style.margin = '0';
      body.style.padding = '0';
      body.style.overflow = 'hidden';

      body.appendChild(div);

      // Custom elements must be registered in each iframe's own window.
      const iframeHead = this.$el.contentDocument.head;
      loadScript('https://cdn.jsdelivr.net/npm/ionicons@latest/dist/ionicons/ionicons.esm.js', {
        target: iframeHead,
        type: 'module'
      });
      loadScript('https://cdn.jsdelivr.net/npm/ionicons@latest/dist/ionicons/ionicons.js', {
        target: iframeHead,
        nomodule: true
      });

      const FormIframe = new Vue({
        name: 'FormIframe',
        store,
        i18n,
        data: { children: Object.freeze(children) },
        render(h) {
          return h('div', this.children);
        }
      });

      FormIframe.$mount(div);
      this.FormIframe = FormIframe;

      this.applyCss();

      // Set up observers after content is rendered
      this.$nextTick(() => {
        this.setupObservers();
        this.setIframeHeight(true); // Mark as initial render
      });
    },

    applyCss() {
      const iframe = this.$el.contentDocument;
      const styleElement = document.createElement('style');
      styleElement.textContent = css + staticFormCss + nunito;
      iframe.head.appendChild(styleElement);

      // Wait for styles to apply before measuring
      setTimeout(() => this.setIframeHeight(), 50);
    },

    setIframeHeight() {
      if (this.isResizing) return; // Prevent recursive calls

      // Clear any pending resize
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        this.isResizing = true;
        this.performResize();
        this.isResizing = false;
      }, 50); // Debounce resizes
    },

    performResize() {
      const iframe = this.$el.contentDocument;
      if (!iframe || !iframe.body) return;

      iframe.body.style.overflow = 'hidden';

      // Measure the content wrapper div directly instead of setting iframe
      // height to 'auto' (which causes a visible page jump for one frame)
      const contentDiv = iframe.body.firstElementChild;
      if (!contentDiv) return;

      const contentHeight = contentDiv.scrollHeight;
      const newHeight = Math.max(contentHeight, 164);

      if (Math.abs(newHeight - this.iframeHeight) > 2) {
        this.iframeHeight = newHeight;
        this.$emit('resize', newHeight);
      }
    },

    setupObservers() {
      this.cleanupObservers(); // Clean up any existing observers

      const target = this.$el.contentDocument.body;
      if (!target) return;

      // Mutation Observer for DOM changes
      if (MutationObserver) {
        this.mutationObserver = new MutationObserver((mutations) => {
          // Check if any mutations actually affect layout
          const shouldResize = mutations.some((mutation) => {
            // Check for added/removed nodes
            if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
              return true;
            }
            // Check for attribute changes that might affect size
            if (mutation.type === 'attributes') {
              const sizeAttributes = ['class', 'style', 'width', 'height'];
              return sizeAttributes.includes(mutation.attributeName);
            }
            return false;
          });

          if (shouldResize) {
            this.setIframeHeight();
          }
        });

        this.mutationObserver.observe(target, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style', 'width', 'height']
        });
      }

      // ResizeObserver for element size changes (if available)
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.setIframeHeight();
        });
        this.resizeObserver.observe(target);
      }

      // Also observe the main content div if it exists
      const contentDiv = target.querySelector('div');
      if (contentDiv && this.resizeObserver) {
        this.resizeObserver.observe(contentDiv);
      }
    },

    cleanupObservers() {
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = null;
      }
    }
  },

  beforeUpdate() {
    if (!this.FormIframe) {
      return;
    }
    this.FormIframe.children = Object.freeze(this.$slots.default);

    // Trigger resize check after update
    this.$nextTick(() => {
      this.setIframeHeight();
    });
  },

  mounted() {
    // Initial setup will be done in renderChildren after iframe loads
  },

  beforeDestroy() {
    this.cleanupObservers();
    if (this.FormIframe) {
      this.FormIframe.$destroy();
    }
  }
};
</script>
